import AdresseApi from 'services/KjederegisteretAdminApi/AdresseApi';
import { ChangeRequestCommand } from 'services/KjederegisteretAdminApi/ChangeRequestApi/types';
import { EndringsMelding } from 'services/QAApi/types';
import { ChangeRequestCommandHelper } from './ChangeRequestCommandHelper';
import { ChangeRequestValidator } from './ChangeRequestValidator';
import { AddressComparer } from './AddressComparer';
import DateHelper from 'utils/DateHelper';

export class ChangeRequestHelper {
  static async copyFromChangeRequestToCommand(
    changeRequest: EndringsMelding,
    command: ChangeRequestCommand,
  ) {
    // This method is a placeholder for any future logic that might be needed
    // to copy values from a change request to a command.
    // Currently, it does not perform any operations.
    if (changeRequest.changes.nedlagtDato && !command.nedlagtDato) {
      ChangeRequestCommandHelper.transferNedlagtDato(command, changeRequest.changes.nedlagtDato);
    }

    if (
      changeRequest.changes.leverandorKundenummer &&
      !command.leverandorKundenummer.includes(changeRequest.changes.leverandorKundenummer)
    ) {
      ChangeRequestCommandHelper.transferKundeNr(
        command,
        changeRequest.changes.leverandorKundenummer,
      );
    }

    if (changeRequest.changes.enhetnavn && !command.enhetnavn) {
      ChangeRequestCommandHelper.transferEnhetnavn(command, changeRequest.changes.enhetnavn);
    }

    if (
      changeRequest.changes.gln &&
      !command.gln &&
      ChangeRequestValidator.isGlnValid(changeRequest.changes.gln, changeRequest)
    ) {
      ChangeRequestCommandHelper.transferGln(command, changeRequest.changes.gln, changeRequest);
    }

    if (
      changeRequest.changes.organisasjonsnr &&
      !command.organisasjonsnr &&
      ChangeRequestValidator.isOrganisasjonsnrValid(changeRequest.changes.organisasjonsnr)
    ) {
      ChangeRequestCommandHelper.transferOrganisasjonsnr(
        command,
        changeRequest.changes.organisasjonsnr,
        changeRequest.changes.juridisknavn,
      );
    }

    if (
      changeRequest.changes.telefon &&
      !command.telefon &&
      !ChangeRequestValidator.hasTelephoneErrors(changeRequest.changes.telefon)
    ) {
      ChangeRequestCommandHelper.transferTelefon(command, changeRequest.changes.telefon);
    }

    if (
      changeRequest.changes.fakturaEpost &&
      !command.fakturaEpost &&
      !ChangeRequestValidator.hasFakturaEpostErrors(changeRequest.changes.fakturaEpost)
    ) {
      ChangeRequestCommandHelper.transferFakturaEpost(command, changeRequest.changes.fakturaEpost);
    }

    if (
      changeRequest.changes.epost &&
      !command.epost &&
      !ChangeRequestValidator.hasEpostErrors(changeRequest.changes.epost)
    ) {
      ChangeRequestCommandHelper.transferEpost(command, changeRequest.changes.epost);
    }

    if (
      changeRequest.changes.hjemmeside &&
      !command.hjemmeside &&
      !ChangeRequestValidator.hasHomepageErrors(changeRequest.changes.hjemmeside)
    ) {
      ChangeRequestCommandHelper.transferHjemmeside(command, changeRequest.changes.hjemmeside);
    }

    if (changeRequest.changes.dagligLeder && !command.dagligLeder) {
      ChangeRequestCommandHelper.transferDagligLeder(command, changeRequest.changes.dagligLeder);
    }

    if (
      changeRequest.changes.omsetning &&
      !command.omsetning &&
      !ChangeRequestValidator.hasOmsetningErrors(changeRequest.changes.omsetning)
    ) {
      ChangeRequestCommandHelper.transferOmsetning(command, changeRequest.changes.omsetning);
    }

    if (
      changeRequest.changes.kontonummer &&
      !command.kontonummer &&
      !ChangeRequestValidator.hasKontonummerErrors(changeRequest.changes.kontonummer)
    ) {
      ChangeRequestCommandHelper.transferKontonummer(command, changeRequest.changes.kontonummer);
    }

    if (
      changeRequest.changes.salgsareal &&
      !command.salgsareal &&
      !ChangeRequestValidator.hasSalgsarealErrors(changeRequest.changes.salgsareal)
    ) {
      ChangeRequestCommandHelper.transferSalgsareal(command, changeRequest.changes.salgsareal);
    }

    if (
      changeRequest.changes.oppstartsDato &&
      !command.oppstartsDato &&
      !ChangeRequestValidator.hasOppstartsDatoErrors(changeRequest.changes.oppstartsDato)
    ) {
      ChangeRequestCommandHelper.transferOppstartsDato(
        command,
        changeRequest.changes.oppstartsDato,
      );
    }

    if (
      changeRequest.changes.grossistKundenummer &&
      !ChangeRequestCommandHelper.grossistKundenummerExists(command, {
        kundenummerHosGrossist: changeRequest.changes.grossistKundenummer,
        grossistGln: changeRequest.changes.grossistKundenummerHosGln,
      }) &&
      changeRequest.changes.grossistKundenummerHosGln &&
      !ChangeRequestValidator.hasGrossistKundenummerErrors(
        changeRequest.changes.grossistKundenummer,
        changeRequest.changes.grossistKundenummerHosGln,
      )
    ) {
      ChangeRequestCommandHelper.transferGrossistKundenummer(command, {
        kundenummerHosGrossist: changeRequest.changes.grossistKundenummer,
        grossistGln: changeRequest.changes.grossistKundenummerHosGln,
      });
    }

    // besoksadresse
    if (changeRequest.changes.besoksadresse && command.besoksadresse) {
      // lookup cr address in AdresseApi
      const lookupCrResults = await AdresseApi.search(
        changeRequest.changes.besoksadresse.formattedForOneLine,
      );

      if (
        !ChangeRequestValidator.hasBesoksAdresseErrors(
          changeRequest.changes.besoksadresse,
          lookupCrResults,
        )
      ) {
        const lookupAddress = lookupCrResults.find((address) =>
          AddressComparer.equals(address, changeRequest.changes.besoksadresse),
        );

        ChangeRequestCommandHelper.transferBesoksadresse(command, lookupAddress);
      }
    }

    // Medlemskap - Markedskjede
    if (changeRequest.membershipSuggestions?.markedsKjede) {
      const markedsKjede = changeRequest.membershipSuggestions.markedsKjede;
      if (changeRequest.changes.medlemTilOgMedDato) {
        // Utmelding
        ChangeRequestCommandHelper.transferMarkedskjedeUtmelding(
          command,
          markedsKjede.nummer,
          changeRequest.changes.medlemTilOgMedDato,
        );
      } else {
        // Innmelding
        ChangeRequestCommandHelper.transferMarkedskjedeInnmelding(
          command,
          markedsKjede.nummer,
          changeRequest.changes.medlemFraOgMedDato ?? DateHelper.getFirstDateOfMonth(),
          changeRequest.changes.medlemsNummer,
        );
      }
    }

    // Medlemskap - Regionalkjede
    if (changeRequest.membershipSuggestions?.regionalKjede) {
      const regionalKjede = changeRequest.membershipSuggestions.regionalKjede;
      if (changeRequest.changes.medlemTilOgMedDato) {
        // Utmelding
        ChangeRequestCommandHelper.transferRegionalkjedeUtmelding(
          command,
          regionalKjede.nummer,
          changeRequest.changes.medlemTilOgMedDato,
        );
      } else {
        // Innmelding
        ChangeRequestCommandHelper.transferRegionalkjedeInnmelding(
          command,
          regionalKjede.nummer,
          changeRequest.changes.medlemFraOgMedDato ?? DateHelper.getFirstDateOfMonth(),
          changeRequest.changes.medlemsNummer,
        );
      }
    }

    // Medlemskap - Innkjøpssamarbeid (Enhetsgruppering)
    if (changeRequest.membershipSuggestions?.innkjopssamarbeid) {
      const innkjopssamarbeid = changeRequest.membershipSuggestions.innkjopssamarbeid;
      if (changeRequest.changes.medlemTilOgMedDato) {
        // Utmelding
        ChangeRequestCommandHelper.transferEnhetsgrupperingUtmelding(
          command,
          Number(innkjopssamarbeid.nummer),
          changeRequest.changes.medlemTilOgMedDato,
        );
      } else {
        // Innmelding
        ChangeRequestCommandHelper.transferEnhetsgrupperingInnmelding(
          command,
          Number(innkjopssamarbeid.nummer),
          changeRequest.changes.medlemFraOgMedDato ?? new Date(),
          changeRequest.changes.medlemsNummer,
        );
      }
    }
  }
}
